import { useMemo } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { formatting } from "@north-beam/nb-common";
import { useClientDataColumns } from "@pages/mmm/hooks/use-client-data-columns";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { Modal } from "@shared/modal";

import { type SpendConstraint } from "../explore-budgets/client-setting/types";
import { formatKpiEquation } from "../model-insights/utils";

interface Props {
  setting: {
    name: string;
    budgetLow?: number;
    budgetHigh?: number;
    kpi?: { name: string; equation: any };
    spendConstraints: SpendConstraint[];
  };
  isOpen: boolean;
  onClose: () => void;
}

export const AboutThisSettingModal = ({ setting, isOpen, onClose }: Props) => {
  const { user } = useUser();
  const { designElementManager } = useDesignElementManager();
  const { clientDataColumns } = useClientDataColumns();
  const { getSpendConstraintFeature } = useClientSettingData();

  const astTokens = useMemo(() => {
    if (!setting.kpi) {
      return [];
    }

    return formatKpiEquation(
      setting.kpi.equation,
      clientDataColumns.filter((d) => d.is_ready_for_use_in_model) as any,
    );
  }, [setting.kpi, clientDataColumns]);

  const hasSpendBudgets =
    setting.budgetLow !== undefined &&
    setting.budgetLow > 0 &&
    setting.budgetHigh !== undefined &&
    setting.budgetHigh > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div className="text-2xl font-bold">About this setting</div>
      }
      bodyClassName="px-0 py-0 bg-v2-primary"
      size="xl"
    >
      <div>
        <div className="flex items-center justify-between"></div>
        <div className="px-5 pb-8 border-b border-v2-tertiary">
          <label className="block text-sm">SETTING NAME</label>
          <div className="text-lg font-bold">{setting.name}</div>
        </div>
        {setting.kpi && (
          <div className="px-5 py-8 border-b border-v2-tertiary">
            <label className="block text-sm">KPI</label>
            <div className="text-lg font-bold">{setting.kpi.name}</div>
            <div className="flex items-center gap-2 mt-3 text-sm">
              {astTokens.map((value, i) => (
                <div
                  key={i}
                  className="flex items-center rounded bg-v2-secondary px-3.5 py-0.5"
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        )}
        {hasSpendBudgets && (
          <div className="px-5 py-8 border-b border-v2-tertiary">
            <label className="block text-sm">TOTAL BUDGET</label>
            <div className="text-lg font-bold">
              {formatting.formatCurrency(setting.budgetLow, {
                precision: "approximate",
                code: user.currencies.currentCurrency,
              })}
              <span className="text-gray+1 mx-1.5">to</span>
              {formatting.formatCurrency(setting.budgetHigh, {
                precision: "approximate",
                code: user.currencies.currentCurrency,
              })}
            </div>
          </div>
        )}
        {setting.spendConstraints.length > 0 && (
          <div className="px-5 py-8 bg-v2-secondary">
            <label className="block text-sm">SPEND REQUIREMENT</label>
            <div className="space-y-3">
              {setting.spendConstraints.map((constraint) => {
                const feature = getSpendConstraintFeature(constraint)!;
                const firstBreakdown = feature.mta_breakdowns[0].value;

                return (
                  <div
                    key={constraint.id}
                    className={clsx(
                      "bg-v2-primary border border-v2-tertiary rounded flex items-center justify-between",
                      "px-3 py-2",
                    )}
                  >
                    <div className="text-lg font-bold flex items-center gap-2 ">
                      {designElementManager
                        .getDesignElement(firstBreakdown)
                        .getComponent({ size: "24px" })}
                      {feature?.friendly_name}
                    </div>
                    <div className="text-sm">
                      {formatting.formatCurrency(constraint.budget_low, {
                        precision: "approximate",
                        code: user.currencies.currentCurrency,
                      })}
                      <span className="mx-1.5 text-gray+1">to</span>
                      {formatting.formatCurrency(constraint.budget_high, {
                        precision: "approximate",
                        code: user.currencies.currentCurrency,
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
